(() => {
  const settings = window.cartThresholdsSettings;
  if (!settings) return;

  const state = window.cartThresholdsState || {};
  window.cartThresholdsState = state;

  const parseAmountToCents = (value) => {
    if (value === null || value === undefined) return 0;
    const cleaned = String(value).replace(/[^0-9,.-]/g, '').replace(',', '.');
    const numberValue = parseFloat(cleaned);
    if (Number.isNaN(numberValue)) return 0;
    return Math.round(numberValue * 100);
  };

  const minAmount = parseAmountToCents(settings.minAmount);
  const freeAmount = parseAmountToCents(settings.freeAmount);

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    const locale = settings.locale || document.documentElement.lang || 'pl-PL';
    const currency = settings.currencyCode || 'PLN';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  const replaceTokens = (template, data) => {
    if (!template) return '';
    return template
      .replace(/{{\s*amount\s*}}/g, data.amount)
      .replace(/{{\s*min\s*}}/g, data.min);
  };

  const getSubtotal = () => {
    if (typeof state.subtotal === 'number') return state.subtotal;
    return 0;
  };

  const getStatus = () => {
    const subtotal = getSubtotal();
    const itemCount = typeof state.itemCount === 'number' ? state.itemCount : null;
    const isEmpty = itemCount === 0;
    const minEnabled = settings.minEnabled && minAmount > 0;
    const freeEnabled = settings.freeEnabled && freeAmount > 0;
    const minRemaining = Math.max(minAmount - subtotal, 0);
    const freeRemaining = Math.max(freeAmount - subtotal, 0);
    const minMet = !minEnabled || subtotal >= minAmount;
    const freeMet = !freeEnabled || subtotal >= freeAmount;
    const showMin = !isEmpty && minEnabled && !minMet;
    const showFree = !isEmpty && freeEnabled && !freeMet && (!showMin || !minEnabled);
    const minBlockActive = !isEmpty && minEnabled && settings.minBlockCheckout && !minMet;

    return {
      subtotal,
      itemCount,
      isEmpty,
      minEnabled,
      freeEnabled,
      minRemaining,
      freeRemaining,
      minMet,
      freeMet,
      showMin,
      showFree,
      minBlockActive,
    };
  };

  const updateContainers = (status) => {
    document.querySelectorAll('[data-cart-thresholds]').forEach((container) => {
      const context = container.dataset.cartThresholdsContext || 'page';
      const minBlock = container.querySelector('[data-cart-threshold-min]');
      const freeBlock = container.querySelector('[data-cart-threshold-free]');
      const minText = container.querySelector('[data-cart-threshold-text]');
      const freeText = container.querySelector('[data-cart-threshold-free-text]');
      const minProgress = container.querySelector('[data-cart-threshold-min-progress]');
      const minProgressBar = container.querySelector('[data-cart-threshold-min-progress-bar]');
      const progress = container.querySelector('[data-cart-threshold-progress]');
      const progressBar = container.querySelector('[data-cart-threshold-progress-bar]');
      const icon = container.querySelector('[data-cart-threshold-icon]');

      container.classList.toggle('cart-thresholds--banner', settings.messageStyle === 'banner');
      container.classList.toggle('cart-thresholds--inline', settings.messageStyle !== 'banner');

      if (icon) {
        const showIcon = context === 'drawer' && settings.showWarningIcon;
        icon.style.display = showIcon ? '' : 'none';
      }

      if (minBlock) {
        minBlock.style.display = status.showMin ? '' : 'none';
        if (minText) {
          minText.textContent = replaceTokens(settings.minMessage, {
            amount: formatMoney(status.minRemaining),
            min: formatMoney(minAmount),
          });
        }
        if (minProgress) {
          const showMinProgress = status.showMin && minAmount > 0;
          minProgress.style.display = showMinProgress ? '' : 'none';
          if (showMinProgress && minProgressBar) {
            const ratio = Math.min(status.subtotal / minAmount, 1);
            const percent = Math.round(ratio * 100);
            minProgressBar.style.width = `${percent}%`;
            minProgress.setAttribute('aria-valuenow', String(percent));
          }
        }
      }

      if (freeBlock) {
        freeBlock.style.display = status.showFree ? '' : 'none';
        if (freeText) {
          freeText.textContent = replaceTokens(settings.freeMessage, {
            amount: formatMoney(status.freeRemaining),
            min: formatMoney(minAmount),
          });
        }
      }

      if (progress) {
        const showProgress = settings.freeShowProgress && status.showFree && freeAmount > 0;
        progress.style.display = showProgress ? '' : 'none';
        if (showProgress && progressBar) {
          const ratio = Math.min(status.subtotal / freeAmount, 1);
          const percent = Math.round(ratio * 100);
          progressBar.style.width = `${percent}%`;
          progress.setAttribute('aria-valuenow', String(percent));
        }
      }

      const shouldHideContainer = !status.showMin && !status.showFree;
      container.classList.toggle('is-hidden', shouldHideContainer);
    });
  };

  const flashAttention = (context) => {
    const container = document.querySelector(
      `[data-cart-thresholds][data-cart-thresholds-context="${context}"]`
    );
    if (!container) return;

    // Przewiń do komunikatu, żeby klient na pewno go zobaczył.
    try {
      container.scrollIntoView({
        behavior: 'smooth',
        block: context === 'drawer' ? 'start' : 'center',
      });
    } catch (error) {
      container.scrollIntoView();
    }

    container.classList.remove('cart-thresholds--attention');
    void container.offsetWidth;
    container.classList.add('cart-thresholds--attention');
    window.clearTimeout(container._cartThresholdAttentionTimer);
    container._cartThresholdAttentionTimer = window.setTimeout(() => {
      container.classList.remove('cart-thresholds--attention');
    }, 1200);
  };

  const updateCheckoutButtons = () => {
    const status = getStatus();
    const minBlocked = status.minBlockActive;
    document.querySelectorAll('[data-cart-terms-submit]').forEach((button) => {
      const termsAccepted = button.dataset.termsAccepted === 'true';
      const shouldDisable = !termsAccepted || minBlocked;
      button.classList.toggle('is-disabled', shouldDisable);
      button.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
    });

    document.querySelectorAll('[data-cart-terms-extra]').forEach((extra) => {
      const termsAccepted = extra.dataset.termsAccepted === 'true';
      const shouldDisable = !termsAccepted || minBlocked;
      extra.classList.toggle('is-disabled', shouldDisable);
    });

    state.minBlockActive = minBlocked;
  };

  window.updateCartCheckoutButtons = updateCheckoutButtons;

  const updateAll = () => {
    const status = getStatus();
    updateContainers(status);
    updateCheckoutButtons();
  };

  const ensureSubtotal = async () => {
    if (typeof state.subtotal === 'number') return;
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
      if (!response.ok) return;
      const cart = await response.json();
      if (typeof cart?.total_price === 'number') {
        state.subtotal = cart.total_price;
      }
      if (typeof cart?.item_count === 'number') {
        state.itemCount = cart.item_count;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const modal = document.querySelector('[data-cart-minimum-modal]');
  const modalText = modal?.querySelector('[data-cart-minimum-modal-text]');
  const modalCloseButtons = modal?.querySelectorAll('[data-cart-minimum-modal-close]') || [];
  let lastActiveElement = null;

  const openModal = () => {
    if (!modal || !settings.minPopupEnabled) return;
    const status = getStatus();
    if (status.minMet) return;
    if (modalText) {
      modalText.textContent = replaceTokens(settings.minPopupText, {
        amount: formatMoney(status.minRemaining),
        min: formatMoney(minAmount),
      });
    }
    lastActiveElement = document.activeElement;
    modal.classList.add('is-active');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-minimum-modal-open');
    const focusTarget = modal.querySelector('[data-cart-minimum-modal-focus]') || modal.querySelector('button');
    focusTarget?.focus();
  };

  const closeModal = () => {
    if (!modal) return;
    modal.classList.remove('is-active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-minimum-modal-open');
    if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
      lastActiveElement.focus();
    }
  };

  modalCloseButtons.forEach((button) => {
    button.addEventListener('click', closeModal);
  });

  if (modal) {
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        closeModal();
      }
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (modal?.classList.contains('is-active')) {
      closeModal();
    }
  });

  const isCartPage = () => {
    if (document.querySelector('form#cart')) return true;
    if (window.routes?.cart_url && window.location.pathname === window.routes.cart_url) return true;
    return false;
  };

  const isDrawerContext = (element) => element?.closest('cart-drawer');

  const handleCheckoutAttempt = async (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest(
      'a[href*="/checkout"], button[name="checkout"], input[name="checkout"], .shopify-payment-button__button'
    );
    if (!trigger) return;

    await ensureSubtotal();
    const status = getStatus();
    if (!status.minBlockActive) return;

    const inDrawer = isDrawerContext(trigger);
    if (inDrawer || isCartPage()) {
      event.preventDefault();
      event.stopPropagation();
      flashAttention(inDrawer ? 'drawer' : 'page');
      return;
    }

    if (!settings.minPopupEnabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    openModal();
  };

  document.addEventListener('click', handleCheckoutAttempt, true);
  document.addEventListener(
    'submit',
    async (event) => {
      if (isCartPage()) return;
      if (!event.target?.querySelector?.('[name="checkout"]')) return;
      await ensureSubtotal();
      const status = getStatus();
      if (!status.minBlockActive) return;
      event.preventDefault();
      event.stopPropagation();
      if (settings.minPopupEnabled) {
        openModal();
      }
    },
    true
  );

  document.addEventListener('DOMContentLoaded', updateAll);
  document.addEventListener('shopify:section:load', updateAll);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cartData = event?.cartData;
      if (cartData && typeof cartData.total_price === 'number') {
        state.subtotal = cartData.total_price;
      } else if (cartData && typeof cartData.items_subtotal_price === 'number') {
        state.subtotal = cartData.items_subtotal_price;
      }
      if (cartData && typeof cartData.item_count === 'number') {
        state.itemCount = cartData.item_count;
      }
      updateAll();
    });
  }
})();
